import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";

const api = {
  list: (q = "") =>
    axiosInstance.get("/api/superadmin/brands/", { params: q ? { q } : {} }),
  create: (body) =>
    axiosInstance.post("/api/superadmin/brands/", body, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, body) =>
    axiosInstance.patch(`/api/superadmin/brands/${id}/`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function StatusBadge({ active }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        background: active ? "#dcfce7" : "#fee2e2",
        color: active ? "#16a34a" : "#dc2626",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: active ? "#22c55e" : "#f43f5e",
        }}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

const Skeleton = ({ width, height = 14 }) => (
  <div
    className="our-brand-skeleton"
    style={{ width, height, borderRadius: 6 }}
  />
);

function BrandTableSkeleton() {
  return Array.from({ length: 6 }, (_, index) => (
    <tr key={`brand-skeleton-${index}`} style={{ borderTop: "1px solid #f1f5f9" }}>
      <td style={{ padding: "12px 20px" }}><Skeleton width={110} height={42} /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width="70%" /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width={28} /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width={68} height={24} /></td>
      <td style={{ padding: "12px 20px", textAlign: "right" }}><div style={{ display: "flex", justifyContent: "flex-end", gap: 7 }}><Skeleton width={32} height={30} /><Skeleton width={82} height={30} /></div></td>
    </tr>
  ));
}

function Modal({ title, children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(15,23,42,.5)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(0,0,0,.18)",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "none",
              color: "#64748b",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "20px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

function LogoPreview({ brand, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "rgba(15,23,42,.8)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${brand.name} logo preview`}
        onClick={(event) => event.stopPropagation()}
        style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}
      >
        <button
          type="button"
          aria-label="Close image preview"
          onClick={onClose}
          style={{
            position: "absolute",
            top: -12,
            right: -12,
            width: 32,
            height: 32,
            border: 0,
            borderRadius: "50%",
            background: "#fff",
            color: "#334155",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            boxShadow: "0 2px 10px rgba(0,0,0,.25)",
          }}
        >
          ×
        </button>
        <img
          src={brand.logo_url}
          alt={`${brand.name} logo`}
          style={{
            display: "block",
            width: "auto",
            height: "auto",
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        />
      </div>
    </div>
  );
}

function BrandForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    sort_order: initial?.sort_order ?? 0,
    is_active: initial?.is_active ?? true,
  });
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!logo && !initial?.id))
      return setError("Brand name and logo are required.");
    onSubmit(form, logo);
  };
  const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    color: "#1e293b",
  };
  return (
    <form onSubmit={submit}>
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 5,
        }}
      >
        Brand name *
      </label>
      <input
        style={input}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="e.g. Roboman"
        autoFocus
      />
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "15px 0 5px",
        }}
      >
        Logo {initial?.id ? "(leave empty to keep current logo)" : "*"}
      </label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setLogo(e.target.files?.[0] || null)}
        style={{ fontSize: 13 }}
      />
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "15px 0 5px",
        }}
      >
        Display order
      </label>
      <input
        type="number"
        min="0"
        style={input}
        value={form.sort_order}
        onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
      />
      <label
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginTop: 15,
          fontSize: 13,
          color: "#374151",
        }}
      >
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
        />{" "}
        Show this brand on the website
      </label>
      {error && (
        <p style={{ color: "#dc2626", fontSize: 12, marginBottom: 0 }}>
          {error}
        </p>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 9,
          marginTop: 22,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "8px 15px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          disabled={saving}
          style={{
            padding: "8px 15px",
            borderRadius: 8,
            border: 0,
            background: "#3b82f6",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {saving ? "Saving..." : initial?.id ? "Save changes" : "Create brand"}
        </button>
      </div>
    </form>
  );
}

export default function OurBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);
  const [previewBrand, setPreviewBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.list(q);
      setBrands(data?.results ?? data?.data ?? []);
    } catch {
      setNotice("Could not load brands.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => load(query), 250);
    return () => clearTimeout(timer);
  }, [query, load]);
  const save = async (form, logo) => {
    setSaving(true);
    try {
      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("sort_order", String(form.sort_order || 0));
      body.append("is_active", String(form.is_active));
      if (logo) body.append("logo", logo);
      if (modal?.id) await api.update(modal.id, body);
      else await api.create(body);
      setModal(null);
      setNotice(`Brand ${modal?.id ? "updated" : "created"} successfully.`);
      load(query);
    } catch (err) {
      setNotice(err?.response?.data?.detail || "Could not save the brand.");
    } finally {
      setSaving(false);
    }
  };
  const toggleStatus = async (brand) => {
    try {
      const body = new FormData();
      body.append("is_active", String(!brand.is_active));
      await api.update(brand.id, body);
      setNotice(`Brand ${brand.is_active ? "deactivated" : "activated"} successfully.`);
      load(query);
    } catch {
      setNotice("Could not update brand status.");
    }
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "28px 30px",
        background: "#f8fafc",
        color: "#1e293b",
        fontFamily: "Inter, Arial, sans-serif",
      }}
    >
      <style>{`@keyframes our-brand-skeleton-pulse{0%,100%{opacity:.55}50%{opacity:1}}.our-brand-skeleton{background:#e2e8f0;animation:our-brand-skeleton-pulse 1.2s ease-in-out infinite}.our-brand-action:hover{background:#f8fafc!important}.our-brand-primary:hover{background:#2563eb!important}.our-brand-logo-trigger:hover{border-color:#93c5fd!important;background:#f8fafc!important}`}</style>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Our Brands
          </h1>
          <p style={{ margin: "5px 0 0", fontSize: 13, color: "#64748b" }}>
            Manage the brand logos displayed across the public website.
          </p>
        </div>
        <button
          className="our-brand-primary"
          onClick={() => setModal({})}
          style={{
            border: 0,
            borderRadius: 8,
            background: "#3b82f6",
            color: "#fff",
            padding: "9px 14px",
            display: "inline-flex",
            gap: 7,
            alignItems: "center",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <PlusIcon /> Create Brand
        </button>
      </div>
      {notice && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,.05)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ position: "relative", width: "min(340px, 100%)" }}>
            <span
              style={{
                position: "absolute",
                left: 11,
                top: 10,
                color: "#94a3b8",
              }}
            >
              <SearchIcon />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brands..."
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "9px 12px 9px 34px",
                border: "1.5px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
          </div>
          <span style={{ fontSize: 13, color: "#64748b", paddingTop: 9 }}>
            {brands.length} brand{brands.length === 1 ? "" : "s"}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8fafc",
                  color: "#64748b",
                  textAlign: "left",
                }}
              >
                <th style={{ padding: "12px 20px", fontWeight: 600 }}>Logo</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Brand name
                </th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Display order
                </th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Status
                </th>
                <th
                  style={{
                    padding: "12px 20px",
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <BrandTableSkeleton />
              ) : !brands.length ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      padding: 36,
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    No brands found. Create your first brand to show its logo on
                    the website.
                  </td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 20px" }}>
                      <button
                        type="button"
                        className="our-brand-logo-trigger"
                        onClick={() => setPreviewBrand(brand)}
                        title={`Preview ${brand.name} logo`}
                        style={{
                          width: 110,
                          height: 42,
                          border: "1px solid #f1f5f9",
                          borderRadius: 7,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#fff",
                          cursor: "zoom-in",
                          padding: 4,
                        }}
                      >
                        <img
                          src={brand.logo_url}
                          alt={`${brand.name} logo`}
                          style={{
                            maxWidth: 92,
                            maxHeight: 30,
                            objectFit: "contain",
                          }}
                        />
                      </button>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        fontWeight: 600,
                        color: "#0f172a",
                      }}
                    >
                      {brand.name}
                    </td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>
                      {brand.sort_order}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge active={brand.is_active} />
                    </td>
                    <td
                      style={{
                        padding: "12px 20px",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <button
                        className="our-brand-action"
                        onClick={() => setModal(brand)}
                        style={{
                          padding: "6px 9px",
                          background: "#fff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 7,
                          color: "#2563eb",
                          cursor: "pointer",
                          marginRight: 7,
                          verticalAlign: "middle",
                        }}
                        title="Edit brand"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="our-brand-action"
                        onClick={() => toggleStatus(brand)}
                        style={{
                          padding: "6px 10px",
                          background: brand.is_active ? "#fef2f2" : "#f0fdf4",
                          border: `1px solid ${brand.is_active ? "#fecaca" : "#bbf7d0"}`,
                          borderRadius: 7,
                          color: brand.is_active ? "#dc2626" : "#15803d",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                          verticalAlign: "middle",
                        }}
                      >
                        {brand.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modal && (
        <Modal
          title={modal.id ? "Edit Brand" : "Create Brand"}
          onClose={() => setModal(null)}
        >
          <BrandForm
            initial={modal.id ? modal : null}
            onSubmit={save}
            onCancel={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}
      {previewBrand && (
        <LogoPreview brand={previewBrand} onClose={() => setPreviewBrand(null)} />
      )}
    </div>
  );
}
